import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ROUTES } from '../../../constants/routes'

import { useFormik, Form, FormikProvider } from 'formik'
import * as Yup from 'yup'

import MuiBox from '@mui/material/Box'
import MuiTypography from '@mui/material/Typography'
import MuiDivider from '@mui/material/Divider'
import MuiButton from '@mui/material/Button'
import MuiLoadingButton from '@mui/lab/LoadingButton'

import { useGetCampaignQuery, useAddCampaignMutation, useUpdateCampaignMutation } from '../slice'
import { useGetClientsQuery } from '../../clients/slice'
import { useGetUsersQuery } from '../../users/slice'

import PageTitle from '../../../components/page-title'
import FormikControl from '../../../components/formik-control'
import LoadingScreen from '../../../components/loading-screen'
import Producers from './producers'
import Representatives from './representatives'
import Layout from './layout'

const CampaignForm = () => {
  const { campaignId } = useParams()
  const navigate = useNavigate()

  const { data: campaign, isLoading: isCampaignLoading } = useGetCampaignQuery(campaignId, { skip: !campaignId })
  const { data: clients, isLoading: isClientsLoading } = useGetClientsQuery()
  const { data: users, isLoading: isUsersLoading } = useGetUsersQuery()

  const [addCampaign] = useAddCampaignMutation()
  const [updateCampaign] = useUpdateCampaignMutation()
  const [ownerOptions, setOwnerOptions] = useState()
  const [clientOptions, setClientOptions] = useState()

  useEffect(() => {
    if (users) {
      const mappedUsers = users.map((user) => ({
        value: user.sub,
        key: `${user.givenName} ${user.familyName}`,
      }))
      const blankOption = { value: '', key: '< No Owner >' }
      mappedUsers.unshift(blankOption)
      setOwnerOptions(mappedUsers)
    }
  }, [users])

  useEffect(() => {
    if (clients) {
      setClientOptions(clients.map((client) => ({ value: client.id, key: client.name })))
    }
  }, [clients])

  const initialValues =
    campaign && ownerOptions && clientOptions
      ? {
          id: campaign.id || undefined,
          code: campaign.code || '',
          name: campaign.name || '',
          description: campaign.description || '',
          owner: campaign.owner || '',
          about: campaign.about || '',
          client: campaign.client || '',
          startDate: campaign.startDate ? new Date(campaign.startDate) : '',
          isActive: campaign.isActive ?? true,
        }
      : {
          id: undefined,
          code: '',
          name: '',
          description: '',
          owner: '',
          about: '',
          client: '',
          startDate: '',
          isActive: true,
        }

  const validationSchema = Yup.object({
    code: Yup.string().required('Required'),
    name: Yup.string().required('Required'),
    client: Yup.string().required('Required'),
  })

  const onSubmit = async (values) => {
    if (campaignId !== 'new') {
      if (formik.dirty) await updateCampaign(values)
      navigate(ROUTES.CAMPAIGNS, { replace: true })
    } else {
      const { data: result } = await addCampaign(values)
      navigate(`${ROUTES.CAMPAIGNS}/${result.data.id}`, { replace: true })
    }
  }

  const handleSaveClick = () => {
    formik.handleSubmit()
  }

  const handleBackClick = () => {
    navigate(ROUTES.CAMPAIGNS)
  }

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
    enableReinitialize: true,
  })

  const action = (
    <>
      <MuiButton variant="outlined" color="error" onClick={handleBackClick} sx={{ ml: 1 }}>
        Back
      </MuiButton>
      <MuiLoadingButton variant="outlined" onClick={handleSaveClick} sx={{ ml: 1 }} loading={formik.isSubmitting}>
        Save
      </MuiLoadingButton>
    </>
  )

  if (isCampaignLoading || isClientsLoading || isUsersLoading || !ownerOptions || !clientOptions)
    return <LoadingScreen />

  return (
    <>
      <PageTitle title={`Campaigns :: ${campaignId === 'new' ? 'New Campaign' : campaign?.name}`} action={action} />
      <MuiBox sx={{ display: 'flex', flexDirection: 'column' }}>
        <FormikProvider value={formik}>
          <Form>
            <MuiTypography variant="h6">IN BRIEF</MuiTypography>
            <MuiBox
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'start',
              }}
            >
              <MuiTypography sx={{ width: 150 }}>Code</MuiTypography>
              <FormikControl control="input" label="Code" name="code" sx={{ width: 100, mt: 1 }} />
            </MuiBox>
            <MuiBox
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'start',
              }}
            >
              {' '}
              <MuiTypography sx={{ width: 150 }}>Name</MuiTypography>
              <FormikControl control="input" label="Name" name="name" sx={{ width: 400, mt: 1 }} />
            </MuiBox>
            <MuiBox
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'start',
              }}
            >
              <MuiTypography sx={{ width: 150 }}>Description</MuiTypography>
              <FormikControl control="input" label="Description" name="description" sx={{ mt: 1, flexGrow: 1 }} />
            </MuiBox>
            <MuiBox
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'start',
              }}
            >
              <MuiTypography sx={{ width: 150 }}>Client</MuiTypography>
              <FormikControl
                control="select"
                label="Client"
                name="client"
                options={clientOptions}
                disabled={!!formik.values.id}
                sx={{ width: 400 }}
              />
            </MuiBox>
            <MuiBox
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'start',
              }}
            >
              <MuiTypography sx={{ width: 150 }}>Owner</MuiTypography>
              <FormikControl control="select" label="Owner" name="owner" options={ownerOptions} sx={{ width: 400 }} />
            </MuiBox>
            <MuiBox
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'start',
              }}
            >
              <MuiTypography sx={{ width: 150 }}>Start Date</MuiTypography>
              <FormikControl control="datepicker" label="Start Date" name="startDate" sx={{ mt: 1, width: 400 }} />
            </MuiBox>
            <MuiBox
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'start',
              }}
            >
              <MuiTypography sx={{ width: 150 }}>&nbsp;</MuiTypography>
              <FormikControl control="checkbox" label="Active" name="isActive" />
            </MuiBox>
            <MuiDivider sx={{ my: 2 }} />
            <MuiTypography variant="h6">ABOUT</MuiTypography>
            <MuiBox sx={{ display: 'flex' }}>
              <FormikControl control="textarea" label="About" rows={4} name="about" sx={{ flexGrow: 1 }} />
            </MuiBox>
          </Form>
        </FormikProvider>
        <MuiDivider sx={{ my: 2 }} />
        <Producers campaign={campaign} />
        <MuiDivider sx={{ my: 2 }} />
        <Representatives campaign={campaign} />
        <MuiDivider sx={{ my: 2 }} />
        <Layout campaign={campaign} />
      </MuiBox>
    </>
  )
}

export default CampaignForm
